<template>
    <div class="mugen-scroll">
        <slot>
            <button class="mugen-scroll__button"
                v-if="showFallbackButton"
                v-on:click="handleClick">
                <template v-if="!isLoading">
                    {{label || $t('common.load_more')}}
                </template>
                <template v-else>
                    {{$t('common.loading')}}
                </template>
            </button>

            <svg class="mugen-scroll__spinner" v-else-if="showSpinner"
                viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <circle v-pre cx="9" cy="9" fill="none" stroke="currentColor" stroke-width="2" r="8" stroke-dasharray="34 12">
                    <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="1s" values="0 9 9;360 9 9" keyTimes="0;1"/>
                </circle>
            </svg>
        </slot>
    </div>
</template>

<script>
// Start loading the next page while the bottom of the list is still this far away:
const PRELOAD_OFFSET = 400;

export default {
    observer: undefined,

    // Where the sentinel was when the last request was fired. Lets us tell whether
    // that request has actually appended anything, see maybeHandle():
    lastHandledOffset: undefined,

    props: {
        handler: {
            type: Function,
            requried: true,
        },
        shouldHandle: {
            type: Boolean,
            requried: false,
            default: true,
        },
        showButton: {
            type: Boolean,
            requried: false,
            default: false,
        },
        isLoading: {
            type: Boolean,
            requried: false,
            default: false,
        },
        label: {
            type: String,
            required: false,
            default: undefined,
        },
        // Never load on scroll, always wait for a click -- for handlers that are
        // too expensive to run without asking:
        manual: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            isStalled: false,
        };
    },

    computed: {
        showFallbackButton() {
            return this.showButton && (this.manual || this.isStalled);
        },

        showSpinner() {
            return this.showButton && this.isLoading;
        },
    },

    watch: {
        // Loading a page usually disables the handler until it's done. By the time
        // it's enabled again the sentinel may still be on screen, and no new
        // intersection event is coming -- so check by hand instead of waiting.
        // Wait for the tick: the rows that just arrived aren't rendered yet, and
        // maybeHandle() needs their height to tell whether anything was added.
        shouldHandle(value) {
            if (value) {
                this.$nextTick(this.maybeHandle);
            }
        },
    },

    created() {
        this.$options.observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) {
                // Scrolled away: whatever went wrong down there may be worth
                // another try once the user comes back.
                this.$options.lastHandledOffset = undefined;
                this.isStalled = false;
                return;
            }

            this.maybeHandle();
        }, {
            rootMargin: `${PRELOAD_OFFSET}px 0px`,
        });
    },

    mounted() {
        this.$options.observer.observe(this.$el);
    },

    beforeDestroy() {
        this.$options.observer.disconnect();
        this.$options.observer = undefined;
    },

    methods: {
        // Offset from the top of the document -- grows as rows are appended above:
        documentOffset() {
            return Math.round(this.$el.getBoundingClientRect().top + window.scrollY);
        },

        isNearViewport() {
            const { top, bottom, width } = this.$el.getBoundingClientRect();

            // An inactive tab pane is display:none and reports an all-zero rect,
            // which would otherwise read as "right at the top of the screen":
            if (width === 0) {
                return false;
            }

            return top - PRELOAD_OFFSET <= window.innerHeight && bottom + PRELOAD_OFFSET >= 0;
        },

        maybeHandle() {
            if (this.manual || !this.shouldHandle || !this.isNearViewport()) {
                return;
            }

            const offset = this.documentOffset();

            // The previous request appended nothing (it failed, or the page was
            // empty) -- stop pulling and let the user retry by hand instead:
            if (offset === this.$options.lastHandledOffset) {
                this.isStalled = true;
                return;
            }

            this.isStalled = false;
            this.$options.lastHandledOffset = offset;
            this.handler();
        },

        handleClick() {
            this.isStalled = false;
            this.handler();
        },
    },
};
</script>

<style lang="scss">
.mugen-scroll {
    display: flex;
    justify-content: center;
    // The sentinel renders nothing while idle: keep a hairline of height so it
    // stays a dependable IntersectionObserver target.
    min-height: 1px;
    &__button {
        appearance: none;
        text-transform: uppercase;
        cursor: pointer;
        margin: 12px auto;
        text-align: center;
        padding: 10px 22px;
        font-weight: 500;
        background: var(--blue-bright-background);
        color: var(--blue-bright);
        border-radius: 8px;
        font-size: 13px;
        &:hover {
            background: rgba(56, 139, 253, 0.2);
        }
    }
    &__spinner {
        width: 22px;
        height: 22px;
        margin: 14px auto;
        color: var(--body-muted-text-color);
    }
}
</style>
